import { normalizeQuestionnaireData } from '../../utils/questionnairePages';
import { wasQuestionVisible } from '../../utils/conditionalVisibility';
import { redactContactInfo } from '../../utils/contactInfo';

const IMPORTANCE_MULTIPLIER = 1.5;

const getOrderedRankingEntries = (answer = [], options = []) => {
  if (!Array.isArray(answer) || answer.length === 0) return [];
  const normalizedOptions = Array.isArray(options) ? options : [];
  return answer
    .map((raw, rankIndex) => {
      const numericIndex = Number(raw);
      let label = '';
      if (Number.isInteger(numericIndex) && numericIndex >= 0 && numericIndex < normalizedOptions.length) {
        label = normalizedOptions[numericIndex];
      } else if (typeof raw === 'string') {
        label = raw;
      }
      const clean = String(label || '').trim();
      if (!clean) return null;
      return { rank: rankIndex + 1, label: clean };
    })
    .filter(Boolean);
};

export const normalizeMaxScoresToTotal = (scores = [], targetTotal = 0) => {
  const normalizedTarget = Math.max(1, Math.round(Number(targetTotal) || 0));
  const normalizedScores = (scores || []).map((score) => {
    const numericValue = Number(score);
    if (!Number.isFinite(numericValue) || numericValue < 0) return 0;
    return Math.round(numericValue);
  });

  if (!normalizedScores.length) return [];
  const currentTotal = normalizedScores.reduce((sum, score) => sum + score, 0);
  if (currentTotal === normalizedTarget) return normalizedScores;

  if (currentTotal <= 0) {
    const base = Math.floor(normalizedTarget / normalizedScores.length);
    let remainder = normalizedTarget - base * normalizedScores.length;
    return normalizedScores.map(() => {
      if (remainder > 0) {
        remainder -= 1;
        return base + 1;
      }
      return base;
    });
  }

  const scaledScores = normalizedScores.map((score) => (score / currentTotal) * normalizedTarget);
  const flooredScores = scaledScores.map((value) => Math.floor(value));
  let remainder = normalizedTarget - flooredScores.reduce((sum, score) => sum + score, 0);

  if (remainder > 0) {
    const order = scaledScores
      .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
      .sort((a, b) => b.fraction - a.fraction);
    for (let i = 0; i < remainder; i += 1) {
      flooredScores[order[i % order.length].index] += 1;
    }
  } else if (remainder < 0) {
    const order = flooredScores
      .map((value, index) => ({ index, value }))
      .sort((a, b) => b.value - a.value);
    let deficit = Math.abs(remainder);
    let cursor = 0;
    while (deficit > 0 && order.length > 0) {
      const current = order[cursor % order.length];
      if (flooredScores[current.index] > 0) {
        flooredScores[current.index] -= 1;
        deficit -= 1;
      }
      cursor += 1;
    }
  }

  // No question that carries weight may floor to 0 points — a 0 max makes it
  // unscorable (the AI's score is clamped to 0, which broke ranking questions
  // whose larger maxScore got scaled down hardest). Lift each such item to 1,
  // borrowing a point from the largest bucket so the sum stays at the target.
  for (let i = 0; i < flooredScores.length; i += 1) {
    if (normalizedScores[i] > 0 && flooredScores[i] === 0) {
      let donor = -1;
      for (let j = 0; j < flooredScores.length; j += 1) {
        if (flooredScores[j] > 1 && (donor === -1 || flooredScores[j] > flooredScores[donor])) {
          donor = j;
        }
      }
      if (donor !== -1) {
        flooredScores[donor] -= 1;
        flooredScores[i] = 1;
      }
    }
  }

  return flooredScores;
};

export const hasOptionScoreMapping = (question) => {
  if (!question || !Array.isArray(question.options) || question.options.length === 0) return false;
  if (['single-choice', 'multiple-choice', 'matrix', 'combobox', 'ranking'].includes(question.type)) return true;
  if (question.type === 'image' && (question.answerType === 'single-choice' || question.answerType === 'multiple-choice')) return true;
  return false;
};

export const calculateAssignedOptionScore = (question, answer, maxScore) => {
  if (!hasOptionScoreMapping(question)) return null;
  if (!Array.isArray(question.optionScores)) return null;

  // Ranking is scored by the AI: it derives the ideal order from the assigned
  // option points (higher = more important) and rates the applicant's chosen
  // order against that. So we defer to the AI score rather than overriding it.
  if (question.type === 'ranking') return null;

  let assignedScore = 0;
  if (question.type === 'matrix' || question.type === 'combobox') {
    const rows = Array.isArray(answer) ? answer : [];
    rows.forEach((row) => {
      if (row && row.value) {
        const optionIndex = question.options.indexOf(row.value);
        if (optionIndex >= 0) {
          assignedScore += Number(question.optionScores[optionIndex] || 0);
        }
      }
    });
  } else if (question.type === 'single-choice' || (question.type === 'image' && question.answerType === 'single-choice')) {
    const optionIndex = question.options.indexOf(answer);
    if (optionIndex >= 0) assignedScore = Number(question.optionScores[optionIndex] || 0);
  } else if (question.type === 'multiple-choice' || (question.type === 'image' && question.answerType === 'multiple-choice')) {
    const selectedValues = Array.isArray(answer) ? answer : [];
    selectedValues.forEach((selectedValue) => {
      const optionIndex = question.options.indexOf(selectedValue);
      if (optionIndex >= 0) {
        assignedScore += Number(question.optionScores[optionIndex] || 0);
      }
    });
  }

  if (!Number.isFinite(assignedScore)) return 0;
  return Math.min(Math.max(0, Math.round(assignedScore)), maxScore);
};

const applyImportanceWeight = (score, question) =>
  question?.important ? Math.round(score * IMPORTANCE_MULTIPLIER) : score;

// The name is collected under `system_name` and structurally dropped from the
// prompt, but applicants frequently repeat their name inside free-text answers
// ("Ich, Max Müller, ...") or a questionnaire stores it under a different field.
// Build a redactor that scrubs the known name (and its parts) AND any contact
// PII from all serialized prompt text, keeping the AI evaluation name- and
// contact-blind. Contact redaction always runs, even when no name is known.
const buildPiiRedactor = (rawName) => {
  const name = String(rawName || '').trim();
  let namePattern = null;

  if (name) {
    // Full name first, then parts. Drop tokens shorter than 3 chars so initials
    // or short particles ("de", "van") don't over-redact unrelated words.
    const tokens = [name, ...name.split(/\s+/)]
      .map((t) => t.trim())
      .filter((t) => t.length >= 3);
    if (tokens.length > 0) {
      // Longest first so the full name is replaced before its individual parts.
      const unique = [...new Set(tokens)].sort((a, b) => b.length - a.length);
      const escaped = unique.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      try {
        // Unicode-aware boundaries so German names (ü, ö, ß) match correctly.
        namePattern = new RegExp(`(?<![\\p{L}\\p{N}])(?:${escaped.join('|')})(?![\\p{L}\\p{N}])`, 'giu');
      } catch (e) {
        // Fallback for engines without lookbehind/unicode property support.
        namePattern = new RegExp(`\\b(?:${escaped.join('|')})\\b`, 'gi');
      }
    }
  }

  return (text) => {
    if (typeof text !== 'string') return text;
    // Contact info first: if the name redactor ran first it could eat the
    // local part of an email ("max@…" → "[Name entfernt]@…"), leaving the
    // domain behind and preventing the email pattern from matching at all.
    const withoutContact = redactContactInfo(text);
    return namePattern ? withoutContact.replace(namePattern, '[Name entfernt]') : withoutContact;
  };
};

const formatAnswer = (q, answer) => {
  if (q.type === 'ranking' && Array.isArray(answer) && Array.isArray(q.options) && answer.length > 0) {
    const ordered = getOrderedRankingEntries(answer, q.options).map((entry) => `${entry.rank}. ${entry.label}`);
    return `Ranking (Priorität des Bewerbers):\n${ordered.join('\n')}`;
  }
  if ((q.type === 'matrix' || q.type === 'combobox') && Array.isArray(answer) && answer.some((r) => r && r.value)) {
    const filledRows = answer.filter((r) => r && r.value);
    const rows = filledRows.map((r, idx) => `${idx + 1}. ${r.label || '(ohne Bezeichnung)'} → ${r.value}`).join('\n');
    return `${q.type === 'combobox' ? 'Combobox' : 'Matrix'} (${filledRows.length} ${filledRows.length === 1 ? 'Eintrag' : 'Einträge'}):\n${rows}`;
  }
  return JSON.stringify(answer);
};

export const buildEvaluationContext = ({ questionnaire, submission }) => {
  const normalized = normalizeQuestionnaireData(questionnaire || {});
  const defaultQuestions = Array.isArray(questionnaire?.defaultQuestions) ? questionnaire.defaultQuestions : [];
  const scoringConfig = questionnaire?.scoring_config || {};

  // Candidate name comes from the `system_name` answer (fallback: denormalized
  // applicantName). It is NEVER sent to Gemini — used only to redact the name
  // from every other answer/question that does reach the prompt.
  const candidateName = String(
    submission?.answers?.system_name || submission?.applicantName || ''
  ).trim();
  // Redacts the candidate's name AND any email/phone contact info from prompt text.
  const redactName = buildPiiRedactor(candidateName);

  const allQuestions = [
    ...defaultQuestions,
    ...(normalized?.questionPages || []).flatMap((p) => p.questions || [])
  ].filter((q) => q.id !== 'system_name');

  const visibleQuestions = allQuestions.filter((q) => wasQuestionVisible(q, submission.answers));
  const questionCount = visibleQuestions.length;
  if (questionCount === 0) {
    return { empty: true };
  }

  // The Gesamtpunktzahl budget is the single source of truth. ALL questions
  // (formal and otherwise — no per-page or default distinction) share one pool;
  // their per-question max scores are normalized to sum to exactly the budget.
  // ⭐ "wichtig" weights a question higher within that pool (×1.5).
  const totalScoreBudget = scoringConfig.totalScore || 100;
  const fallbackPerQuestion = Math.max(1, Math.round(totalScoreBudget / questionCount));
  const normalizedMaxScores = normalizeMaxScoresToTotal(
    visibleQuestions.map((q) => {
      const ms = Number(q?.maxScore);
      const base = Number.isFinite(ms) && ms >= 0 ? Math.round(ms) : fallbackPerQuestion;
      return applyImportanceWeight(base, q);
    }),
    totalScoreBudget
  );
  const questionMaxScoreMap = new Map(
    visibleQuestions.map((q, i) => [String(q.id), normalizedMaxScores[i] ?? fallbackPerQuestion])
  );

  const qaPairs = visibleQuestions
    .filter((q) => q.type !== 'file-upload')
    .map((q, i) => {
      const maxScore = questionMaxScoreMap.get(String(q.id)) ?? fallbackPerQuestion;
      const qText = (q.text || '').replace(/<[^>]*>/g, '').trim() || `Frage ${i + 1}`;
      const qDescription = (q.description || '').replace(/<[^>]*>/g, '').trim();
      const answer = submission.answers[q.id] || 'Keine Antwort';
      const hasOptions = hasOptionScoreMapping(q);

      // PII redaction runs per content fragment (question text, description,
      // options, answer) — NEVER on the assembled line. Redacting the whole
      // line let the phone regex swallow numeric question ids (timestamps like
      // 1749385101911.3408 → "[Telefon entfernt]"), so Gemini never saw the
      // real questionId and none of its scores/reasons could be matched back.
      let info = `[${q.id}] "${redactName(qText)}" (max ${maxScore}P)${q.important ? ' ⭐ WICHTIG' : ''}`;
      if (qDescription) info += `\nZusatzbeschreibung: ${redactName(qDescription)}`;
      if (hasOptions && Array.isArray(q.optionScores)) {
        info += ` | Punkte: ${q.options.map((o, j) => `${redactName(String(o))}=${q.optionScores[j] || 0}`).join(', ')}`;
      }
      if (q.type === 'ranking' && Array.isArray(q.options)) {
        info += ' | Ranking — höhere Punkte = wichtiger. Ideale Reihenfolge: Optionen nach absteigender Punktzahl.';
      }
      if ((q.type === 'matrix' || q.type === 'combobox') && Array.isArray(q.options)) {
        info += ` | Spalten: ${q.options.map((o) => redactName(String(o))).join(', ')}`;
      }
      info += `\nAntwort: ${redactName(formatAnswer(q, answer))}`;

      return { questionId: q.id, question: q, maxScore, info };
    });

  const questionById = new Map(qaPairs.map((p) => [String(p.questionId), p.question]));

  // Ordered page sections (formal/default page inserted at its configured
  // position, then custom pages in order) so the stored per-page breakdown
  // matches the order the reviewer sees in the form / answer tabs.
  const customPages = (normalized?.questionPages || []).filter(
    (p) => Array.isArray(p?.questions) && p.questions.length > 0
  );
  const clampDefaultOrder = (order, customCount) => {
    const maxOrder = Math.max(1, Number(customCount) + 1);
    const numeric = Number(order);
    if (!Number.isFinite(numeric)) return maxOrder;
    return Math.min(Math.max(Math.round(numeric), 1), maxOrder);
  };
  const defaultPageOrder = clampDefaultOrder(
    questionnaire?.defaultQuestionsPageOrder ?? questionnaire?.intro_page?.defaultQuestionsPageOrder,
    customPages.length
  );
  const defaultSectionLabel = String(
    (questionnaire?.intro_page?.defaultSectionTitle ?? questionnaire?.defaultSectionTitle ?? '') || ''
  ).trim() || 'Formale Fragen';

  const pageSections = [];
  let pageCursor = 0;
  const totalSections = customPages.length + 1;
  for (let position = 1; position <= totalSections; position += 1) {
    if (position === defaultPageOrder) {
      pageSections.push({
        key: 'default',
        title: defaultSectionLabel,
        questionIds: defaultQuestions.filter((q) => q.id !== 'system_name').map((q) => String(q.id))
      });
      continue;
    }
    const p = customPages[pageCursor];
    if (p) {
      pageSections.push({
        key: String(p.id),
        title: (p.title || '').trim() || `Seite ${pageCursor + 1}`,
        questionIds: (p.questions || []).map((q) => String(q.id))
      });
    }
    pageCursor += 1;
  }

  return {
    empty: false,
    defaultQuestions,
    visibleQuestions,
    qaPairs,
    questionById,
    questionMaxScoreMap,
    totalScoreBudget,
    scoringConfig,
    pageSections,
    redactName
  };
};
